import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { CalculationMemoryDto, CalculationMemoryGroupDetailsDto } from "@bi/types";

/** Maior página que a API aceita. */
export const MAX_PAGE = 500;
/** Teto de grupos lidos para as abas (clientes/fornecedores): cobre a Transmasi com folga. */
const MAX_GROUPS = 3000;

export function periodQuery(from: string | null, to: string | null): string {
  return from && to ? `&periodFrom=${encodeURIComponent(from)}&periodTo=${encodeURIComponent(to)}` : "";
}

/**
 * A memória de cálculo com TODOS os grupos (clientes, fornecedores, contas),
 * lida em páginas de 500 do mesmo endpoint que a tela já usa.
 */
export async function fetchMemoryAllGroups(cardKey: string, periodQS: string): Promise<CalculationMemoryDto> {
  const base = `/calculation-memory/${encodeURIComponent(cardKey)}?limit=${MAX_PAGE}&sortBy=value_desc${periodQS}`;
  const first = await api.get<CalculationMemoryDto>(`${base}&offset=0`);
  const rows = [...first.breakdown.rows];
  const total = Math.min(first.breakdown.pagination.totalGroups, MAX_GROUPS);
  for (let offset = MAX_PAGE; offset < total; offset += MAX_PAGE) {
    const page = await api.get<CalculationMemoryDto>(`${base}&offset=${offset}`);
    rows.push(...page.breakdown.rows);
  }
  return { ...first, breakdown: { ...first.breakdown, rows } };
}

export function useMemoryAllGroups(cardKey: string, from: string | null, to: string | null, enabled = true) {
  const periodQS = periodQuery(from, to);
  return useQuery({
    queryKey: ["calculation-memory-all", cardKey, from, to],
    queryFn: () => fetchMemoryAllGroups(cardKey, periodQS),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function fetchGroupDetails(cardKey: string, groupKey: string, periodQS: string, limit = MAX_PAGE, offset = 0) {
  return api.get<CalculationMemoryGroupDetailsDto>(
    `/calculation-memory/${encodeURIComponent(cardKey)}/group/${encodeURIComponent(groupKey)}/details?limit=${limit}&offset=${offset}${periodQS}`,
  );
}

export function downloadText(filename: string, content: string, type = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
